import { useState } from 'react';

import { getMoonPhaseEmoji } from '@/services/celestial-service';
import type { MoonPhaseName } from '@/services/celestial-service';

function formatMoonPhaseName(phase: MoonPhaseName): string {
  return phase
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatTime(isoTime: string | null | undefined): string {
  if (!isoTime) return '—';
  const date = new Date(isoTime);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

interface MoonPhaseDisplayProps {
  phase: MoonPhaseName;
  illumination: number;
  rise: string | null;
  set: string | null;
  compact?: boolean;
}

export function MoonPhaseDisplay({ phase, illumination, rise, set, compact = false }: MoonPhaseDisplayProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const emoji = getMoonPhaseEmoji(phase);
  const phaseName = formatMoonPhaseName(phase);

  if (compact) {
    return (
      <div className="relative inline-flex items-center gap-1">
        <span className="text-body-sm text-[var(--color-text-primary)]">
          {emoji} {phaseName} · {illumination}%
        </span>
        <button
          type="button"
          onClick={() => setShowTooltip((v) => !v)}
          aria-label="Moon phase info"
          data-testid="moon-phase-info-button"
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors flex-shrink-0 leading-none"
        >
          ⓘ
        </button>
        {showTooltip && (
          <div
            className="absolute left-0 top-full mt-1 z-10 bg-[var(--color-elevated)] border border-[var(--color-border)] rounded-lg p-3 text-body-sm text-[var(--color-text-secondary)] max-w-xs shadow-lg"
            data-testid="moon-phase-tooltip"
            role="tooltip"
          >
            Moon phase is displayed for reference. Current research shows limited effect on deer movement compared to weather and time of day.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-body-sm text-[var(--color-text-primary)] font-medium">
        {emoji} {phaseName} · {illumination}% illumination
      </p>
      <div className="flex gap-4 text-body-sm text-[var(--color-text-secondary)]">
        <span>Moonrise: {formatTime(rise)}</span>
        <span>Moonset: {formatTime(set)}</span>
      </div>
      <p className="text-body-xs text-[var(--color-text-muted)] leading-relaxed mt-1">
        Moon phase has minimal effect on deer movement during legal shooting hours (MSU Deer Lab, 2025)
      </p>
    </div>
  );
}
